import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, X, Check, AlertCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export const CameraSelfie = ({ onCapture, onCancel }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const prevFrameRef = useRef(null);
  const streamRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [permissionPrompt, setPermissionPrompt] = useState(true);
  const [faceDetected, setFaceDetected] = useState(false);
  const [isStable, setIsStable] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [capturedImage, setCapturedImage] = useState(null);
  const stableCountRef = useRef(0);
  const detectionIntervalRef = useRef(null);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (detectionIntervalRef.current) {
      clearInterval(detectionIntervalRef.current);
      detectionIntervalRef.current = null;
    }
    setCameraActive(false);
  }, []);

  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  const startCamera = async () => {
    setPermissionPrompt(false);
    setCameraError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);

      // Start face/stability detection
      setTimeout(() => startDetection(), 500);
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setCameraError('Camera access denied. Please allow camera access in your browser settings and try again.');
      } else if (err.name === 'NotFoundError') {
        setCameraError('No camera found on this device. Please connect a camera and try again.');
      } else {
        setCameraError(`Camera error: ${err.message}`);
      }
    }
  };

  const startDetection = () => {
    if (detectionIntervalRef.current) clearInterval(detectionIntervalRef.current);

    detectionIntervalRef.current = setInterval(() => {
      if (!videoRef.current || !canvasRef.current) return;

      const video = videoRef.current;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');

      canvas.width = 160;
      canvas.height = 120;
      ctx.drawImage(video, 0, 0, 160, 120);

      const currentFrame = ctx.getImageData(0, 0, 160, 120);

      // Check center region for presence (skin-tone detection)
      const centerX = 80, centerY = 60, radius = 30;
      let skinPixels = 0;
      let totalPixels = 0;

      for (let y = centerY - radius; y < centerY + radius; y++) {
        for (let x = centerX - radius; x < centerX + radius; x++) {
          const i = (y * 160 + x) * 4;
          const r = currentFrame.data[i];
          const g = currentFrame.data[i + 1];
          const b = currentFrame.data[i + 2];

          // Simple skin color detection (works for various skin tones)
          if (r > 60 && g > 40 && b > 20 && r > g && r > b &&
              Math.abs(r - g) > 15 && r - b > 15) {
            skinPixels++;
          }
          totalPixels++;
        }
      }

      const skinRatio = skinPixels / totalPixels;
      const facePresent = skinRatio > 0.15;

      setFaceDetected(facePresent);

      // Motion detection for stability
      if (prevFrameRef.current && facePresent) {
        let diffSum = 0;
        const prevData = prevFrameRef.current.data;
        const currData = currentFrame.data;
        const len = Math.min(prevData.length, currData.length);

        for (let i = 0; i < len; i += 16) {
          diffSum += Math.abs(currData[i] - prevData[i]);
        }

        const avgDiff = diffSum / (len / 16);
        const isStill = avgDiff < 8;

        if (isStill) {
          stableCountRef.current++;
        } else {
          stableCountRef.current = Math.max(0, stableCountRef.current - 2);
        }

        setIsStable(stableCountRef.current >= 5);
      }

      prevFrameRef.current = currentFrame;
    }, 200);
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;

    const video = videoRef.current;
    const captureCanvas = document.createElement('canvas');
    captureCanvas.width = video.videoWidth;
    captureCanvas.height = video.videoHeight;
    const ctx = captureCanvas.getContext('2d');

    // Mirror the image (selfie mode)
    ctx.translate(captureCanvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);

    const dataUrl = captureCanvas.toDataURL('image/jpeg', 0.85);
    setCapturedImage(dataUrl);
    stopCamera();

    // Convert to blob for file handling
    captureCanvas.toBlob((blob) => {
      const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
      onCapture(file, dataUrl);
    }, 'image/jpeg', 0.85);
  };

  const retake = () => {
    setCapturedImage(null);
    setFaceDetected(false);
    setIsStable(false);
    stableCountRef.current = 0;
    prevFrameRef.current = null;
    startCamera();
  };

  // Captured image view
  if (capturedImage) {
    return (
      <Card className="bg-white border border-gray-200 p-4 rounded-xl" data-testid="selfie-captured">
        <div className="relative aspect-square max-w-sm mx-auto rounded-xl overflow-hidden bg-black">
          <img src={capturedImage} alt="Selfie" className="w-full h-full object-cover" />
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-3">
            <Button
              onClick={retake}
              variant="outline"
              className="bg-white/90 border-gray-300 text-gray-700"
              data-testid="selfie-retake-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retake
            </Button>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 mt-3 text-green-600">
          <Check className="w-5 h-5" />
          <span className="font-medium">Selfie captured successfully</span>
        </div>
      </Card>
    );
  }

  // Permission prompt
  if (permissionPrompt && !cameraActive) {
    return (
      <Card className="bg-white border border-gray-200 p-6 rounded-xl text-center" data-testid="selfie-permission">
        <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
          <Camera className="w-8 h-8 text-blue-600" />
        </div>
        <h3 className="font-syne text-lg font-bold text-gray-900 mb-2">Camera Access Required</h3>
        <p className="text-gray-600 text-sm mb-2">
          To complete your KYC verification, we need to take a selfie to confirm your identity.
        </p>
        <p className="text-gray-500 text-xs mb-4">
          Please allow camera access when prompted by your browser. Your selfie will only be used for identity verification purposes.
        </p>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
            <p className="text-yellow-800 text-xs text-left">
              When your browser asks to use your camera, tap <strong>"Allow"</strong> to proceed. 
              Make sure you are in a well-lit area and remove any headwear or sunglasses.
            </p>
          </div>
        </div>
        <div className="flex gap-3 justify-center">
          <Button
            onClick={startCamera}
            className="bg-afriperx-green hover:bg-afriperx-green-dark text-white"
            data-testid="selfie-open-camera-btn"
          >
            <Camera className="w-4 h-4 mr-2" />
            Open Camera
          </Button>
          {onCancel && (
            <Button onClick={onCancel} variant="outline" className="border-gray-300 text-gray-600">
              Cancel
            </Button>
          )}
        </div>
      </Card>
    );
  }

  // Camera error
  if (cameraError) {
    return (
      <Card className="bg-white border border-red-200 p-6 rounded-xl text-center" data-testid="selfie-error">
        <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h3 className="font-syne text-lg font-bold text-gray-900 mb-2">Camera Error</h3>
        <p className="text-gray-600 text-sm mb-4">{cameraError}</p>
        <div className="flex gap-3 justify-center">
          <Button onClick={startCamera} className="bg-afriperx-green hover:bg-afriperx-green-dark text-white">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
          {onCancel && (
            <Button onClick={onCancel} variant="outline" className="border-gray-300 text-gray-600">
              Cancel
            </Button>
          )}
        </div>
      </Card>
    );
  }

  // Live camera view
  return (
    <Card className="bg-white border border-gray-200 p-4 rounded-xl" data-testid="selfie-camera-active">
      <div className="relative aspect-square max-w-sm mx-auto rounded-xl overflow-hidden bg-black">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full h-full object-cover"
          style={{ transform: 'scaleX(-1)' }}
        />

        {/* Face guide oval overlay */}
        <div className="absolute inset-0 pointer-events-none">
          <svg viewBox="0 0 400 400" className="w-full h-full">
            <defs>
              <mask id="faceMask">
                <rect width="400" height="400" fill="white" />
                <ellipse cx="200" cy="180" rx="100" ry="130" fill="black" />
              </mask>
            </defs>
            <rect width="400" height="400" fill="rgba(0,0,0,0.4)" mask="url(#faceMask)" />
            <ellipse
              cx="200" cy="180" rx="100" ry="130"
              fill="none"
              stroke={isStable ? '#22c55e' : faceDetected ? '#eab308' : '#ffffff'}
              strokeWidth="3"
              strokeDasharray={isStable ? 'none' : '8 4'}
            />
          </svg>
        </div>

        {/* Status indicator */}
        <div className="absolute bottom-3 left-0 right-0 text-center">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
            isStable ? 'bg-green-500 text-white' :
            faceDetected ? 'bg-yellow-500 text-white' :
            'bg-gray-800/80 text-white'
          }`}>
            {isStable ? (
              <><Check className="w-4 h-4" /> Face steady - Capture now</>
            ) : faceDetected ? (
              <><AlertCircle className="w-4 h-4" /> Hold still...</>
            ) : (
              <><Camera className="w-4 h-4" /> Position your face in the oval</>
            )}
          </div>
        </div>
      </div>

      {/* Hidden canvas for frame analysis */}
      <canvas ref={canvasRef} className="hidden" />

      {/* Controls */}
      <div className="flex gap-3 justify-center mt-4">
        <Button
          onClick={capturePhoto}
          disabled={!isStable}
          className={`${isStable ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-400'} text-white px-8`}
          data-testid="selfie-capture-btn"
        >
          <Camera className="w-4 h-4 mr-2" />
          {isStable ? 'Capture Selfie' : 'Hold Still...'}
        </Button>
        <Button
          onClick={() => { stopCamera(); onCancel?.(); }}
          variant="outline"
          className="border-gray-300 text-gray-600"
        >
          <X className="w-4 h-4 mr-2" />
          Cancel
        </Button>
      </div>
    </Card>
  );
};
